import styles from 'css/src/components/common/mentions/ReferenceMention.module.scss';

import React from 'react';
import ReactDOM from 'react-dom';
import classNames from 'classnames';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $getNearestNodeFromDOMNode, $getNodeByKey, $getRoot } from 'lexical';

import { KIND_LABEL } from './referenceKinds';
import type { Reference, ReferenceKind } from './ReferenceMentionNode';
import { $createReferenceMentionNode, $isReferenceMentionNode } from './ReferenceMentionNode';
import type { ReferencePopoverPosition } from './referencePopoverPosition';
import { referencePopoverPosition } from './referencePopoverPosition';
import ReferencePreviewCard from './ReferencePreviewCard';
import ReferenceRowIcon from './ReferenceRowIcon';

interface ActiveReference extends ReferencePopoverPosition {
    kind: ReferenceKind;
    id: string;
    name: string;
    /** Pins the swap to the pill that was clicked — the same object can be
     * mentioned more than once. */
    nodeKey: string | null;
    /** True once clicked, which turns the preview into a swap picker. */
    isPicking: boolean;
}

interface ReferenceMentionPopoverPluginProps {
    references: Reference[];
    /** Kinds whose swap happens in a modal rather than the inline dropdown. */
    browseKinds?: ReferenceKind[];
    onBrowse?: (kind: ReferenceKind, currentId: string, currentNodeKey: string | null) => void;
}

/**
 * Hover previews a mention pill; clicking turns that into a picker of the same
 * kind. Delegated off the editor root and driven by the `data-*` attributes
 * `ReferenceMentionNode.createDOM` sets, so it never reads Lexical state.
 */
const ReferenceMentionPopoverPlugin: React.FC<ReferenceMentionPopoverPluginProps> = ({
    references,
    browseKinds = [],
    onBrowse,
}) => {
    const [editor] = useLexicalComposerContext();
    const [active, setActive] = React.useState<ActiveReference | null>(null);

    const readPill = React.useCallback(
        (pill: HTMLElement, isPicking: boolean): ActiveReference => {
            return {
                kind: (pill.getAttribute('data-reference-kind') ?? 'video') as ReferenceKind,
                id: pill.getAttribute('data-reference-id') ?? '',
                name: pill.getAttribute('data-reference-name') ?? '',
                nodeKey: editor.read(() => $getNearestNodeFromDOMNode(pill)?.getKey() ?? null),
                ...referencePopoverPosition(pill.getBoundingClientRect()),
                isPicking,
            };
        },
        [editor],
    );

    React.useEffect(() => {
        const handleMouseOver = (event: MouseEvent) => {
            const pill = (event.target as HTMLElement | null)?.closest<HTMLElement>(
                '[data-reference-id]',
            );
            if (!pill) {
                return;
            }
            setActive((current) => (current?.isPicking ? current : readPill(pill, false)));
        };

        const handleMouseOut = (event: MouseEvent) => {
            const pill = (event.target as HTMLElement | null)?.closest('[data-reference-id]');
            if (!pill) {
                return;
            }
            const related = event.relatedTarget as Node | null;
            if (related && pill.contains(related)) {
                return;
            }
            setActive((current) => (current?.isPicking ? current : null));
        };

        const handleClick = (event: MouseEvent) => {
            const pill = (event.target as HTMLElement | null)?.closest<HTMLElement>(
                '[data-reference-id]',
            );
            if (!pill) {
                return;
            }
            event.preventDefault();
            const clicked = readPill(pill, true);
            // Kinds with too many options for a small dropdown open the modal.
            if (onBrowse && browseKinds.includes(clicked.kind)) {
                setActive(null);
                onBrowse(clicked.kind, clicked.id, clicked.nodeKey);
                return;
            }
            setActive(clicked);
        };

        return editor.registerRootListener((rootElement, prevRootElement) => {
            if (prevRootElement) {
                prevRootElement.removeEventListener('mouseover', handleMouseOver);
                prevRootElement.removeEventListener('mouseout', handleMouseOut);
                prevRootElement.removeEventListener('click', handleClick);
            }
            if (rootElement) {
                rootElement.addEventListener('mouseover', handleMouseOver);
                rootElement.addEventListener('mouseout', handleMouseOut);
                rootElement.addEventListener('click', handleClick);
            }
        });
    }, [editor, readPill, browseKinds, onBrowse]);

    React.useEffect(() => {
        if (!active?.isPicking) {
            return;
        }
        const handleDocumentClick = (event: MouseEvent) => {
            const target = event.target as HTMLElement | null;
            if (
                target?.closest('[data-reference-popover]') ||
                target?.closest('[data-reference-id]')
            ) {
                return;
            }
            setActive(null);
        };
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setActive(null);
            }
        };
        document.addEventListener('click', handleDocumentClick);
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('click', handleDocumentClick);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [active?.isPicking]);

    const swapTo = React.useCallback(
        (replacement: Reference) => {
            editor.update(() => {
                // The clicked pill's node, falling back to a kind+id search if
                // it has gone (mention pills are token nodes, so the key is
                // normally stable).
                const byKey = active?.nodeKey ? $getNodeByKey(active.nodeKey) : null;
                const match = $isReferenceMentionNode(byKey)
                    ? byKey
                    : $getRoot()
                          .getAllTextNodes()
                          .find(
                              (node) =>
                                  $isReferenceMentionNode(node) &&
                                  node.getReferenceKind() === active?.kind &&
                                  node.getReferenceId() === active?.id,
                          );
                if (match) {
                    match.replace($createReferenceMentionNode(replacement));
                }
            });
            setActive(null);
        },
        [editor, active],
    );

    if (!active) {
        return null;
    }

    const sameKind = references.filter((reference) => reference.kind === active.kind);
    const preview = sameKind.find((reference) => reference.id === active.id);

    return ReactDOM.createPortal(
        <div
            className={styles['reference-popover']}
            style={{ left: active.left, bottom: active.bottom, top: active.top }}
            data-reference-popover
        >
            {active.isPicking ? (
                <>
                    <div className={styles['reference-popover__header']}>
                        Swap {KIND_LABEL[active.kind].toLowerCase()}
                    </div>
                    <ul className={styles['reference-popover__list']}>
                        {sameKind.map((reference) => (
                            <li key={reference.id}>
                                <button
                                    type="button"
                                    className={classNames(styles['reference-popover__option'], {
                                        [styles['selected']]: reference.id === active.id,
                                    })}
                                    onClick={() => swapTo(reference)}
                                >
                                    <ReferenceRowIcon reference={reference} />
                                    <span className={styles['reference-menu-item__text']}>
                                        <span className={styles['reference-menu-item__name']}>
                                            {reference.name}
                                        </span>
                                        <span className={styles['reference-menu-item__detail']}>
                                            {reference.detail
                                                ? `${KIND_LABEL[reference.kind]} · ${reference.detail}`
                                                : KIND_LABEL[reference.kind]}
                                        </span>
                                    </span>
                                    {reference.id === active.id && (
                                        <i translate="no" className="material-symbols-outlined">
                                            check
                                        </i>
                                    )}
                                </button>
                            </li>
                        ))}
                    </ul>
                </>
            ) : (
                <ReferencePreviewCard
                    kind={active.kind}
                    name={active.name}
                    reference={preview}
                    hint="click to change"
                />
            )}
        </div>,
        document.body,
    );
};

export default ReferenceMentionPopoverPlugin;
